/**
 * @file Route table.
 *
 * React Router 8 is ESM-only and `react-router-dom` no longer exists:
 * `createBrowserRouter` comes from `react-router` and `RouterProvider` from
 * `react-router/dom`.
 *
 * Every page is loaded lazily so the landing page does not carry the map,
 * database and analysis code that only the dashboard needs.
 */

import {Suspense, lazy} from 'react';
import type {ComponentType, ReactElement} from 'react';
import {createBrowserRouter} from 'react-router';

import {RouteErrorBoundary} from '@/routes/error-boundary.js';
import {RouteFallback} from '@/routes/route-fallback.js';

/**
 * Wraps a lazily loaded page in the shared loading fallback.
 *
 * @param Component Lazily loaded page component.
 * @returns The element to render for the route.
 */
function withFallback(Component: ComponentType): ReactElement {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Component />
    </Suspense>
  );
}

const LandingPage = lazy(async () => ({
  default: (await import('@/routes/landing.js')).LandingPage,
}));
const DashboardPage = lazy(async () => ({
  default: (await import('@/routes/dashboard.js')).DashboardPage,
}));
const ActivityPage = lazy(async () => ({
  default: (await import('@/routes/activity.js')).ActivityPage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import('@/routes/settings.js')).SettingsPage,
}));
const CreatePage = lazy(async () => ({
  default: (await import('@/routes/create.js')).CreatePage,
}));
const LibraryPage = lazy(async () => ({
  default: (await import('@/routes/library.js')).LibraryPage,
}));
const NotFoundPage = lazy(async () => ({
  default: (await import('@/routes/not-found.js')).NotFoundPage,
}));

/** The application router. */
export const router = createBrowserRouter([
  {
    path: '/',
    element: withFallback(LandingPage),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/dashboard',
    element: withFallback(DashboardPage),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/activity',
    element: withFallback(ActivityPage),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/settings',
    element: withFallback(SettingsPage),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/create',
    element: withFallback(CreatePage),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/library',
    element: withFallback(LibraryPage),
    errorElement: <RouteErrorBoundary />,
  },
  {path: '*', element: withFallback(NotFoundPage)},
]);
